import * as React from 'react';
import { connect } from 'react-redux';

import '../Main.scss';

import {
    Col,
    Container,
    Row,
} from 'reactstrap'

import { 
    Metadata,
    Submission, 
    SubmissionRequestSummary,
    PortalTextCommon,
    PortalTextCommonImport,
    PortalTextSubmissionRequestSummary,
} from '../../Models/Api/strongbox.financialportal';
import { PortalSubmissionRequestFromSubmissionRequestSummary } from '../../Models/PortalCreateSubmissionRequestOptions';
import { knownAccountingGroupIds } from '../../Models/AccountingCollectionGroup';

import { ApplicationState } from '../../Store';
import { GetUserById, LoadingUsers } from '../../Store/User';
import { GetPortalText } from '../../Store/PortalAssets';
import {
    GetDisableAdditionalDocUpload,
    GetDisableProvideuserCopy,
    GetDisableAnonymizeResults,
    GetDisableBasisOfAccounting,
    GetDefaultSubmissionOptions,
} from '../../Store/AppSettings';

import { ConnectionSourceText } from '../../Utils/Constants';
import { GetBasisOfAccountingDescription, GetCollectionRangeSummary } from '../../Utils/GlobalMessageUtils';
import { FormatDate, formatStringMDYShort } from '../../Utils/DateUtils';

import {
    PortalCreateSubmissionRequestOptions
} from '../../Models/PortalCreateSubmissionRequestOptions';

type InjectedReduxState = {
    loadingUsers: boolean;
    userDisplayName?: string;
    fileUploadDisabled: boolean;
    provideUserCopyDisabled: boolean;
    anonymizeResultsDisabled: boolean;
    basisOfAccountingDisabled: boolean;
    defaultSubmissionOptions: PortalCreateSubmissionRequestOptions,
    commonText?: PortalTextCommon,
    srsText?: PortalTextSubmissionRequestSummary,
};

type InjectedActionCreators = {};

type RequestSummaryProps = {
    className?: string;
    requestSummary?: SubmissionRequestSummary;
    loadedUserDisplayName?: string;
    submission?: Submission;
    submissionMetadata?: Metadata[];
    showTypicalImportTime?: boolean;
};

type Props = RequestSummaryProps & InjectedReduxState & InjectedReduxState;

export const RequestSummaryComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        className,
        loadingUsers,
        requestSummary,
        submission,
        userDisplayName,
        defaultSubmissionOptions,
        commonText,
        srsText,
    } = props;

    const loadingUsersContent = (): React.ReactElement => {
        return (
            <Row className={'header-content'}>
                <Col>
                    <h3>{commonText?.loading || ''}</h3>
                </Col>
            </Row>
        )
    }

    const collectingPeriodText = (): string | undefined => {
        let result = undefined;

        if (!requestSummary) {
            result = GetCollectionRangeSummary(defaultSubmissionOptions, commonText);
        } else if (
            (requestSummary.mostRecentPeriodMonth !== undefined) &&
            (requestSummary.mostRecentPeriodYear !== undefined)
        ) {
            const rs = PortalSubmissionRequestFromSubmissionRequestSummary(requestSummary);
            result = GetCollectionRangeSummary(rs, commonText);
        }
        return result;
    }

    const basisOfAccounting = React.useMemo(() => {
        if (!!(requestSummary)) {
            return GetBasisOfAccountingDescription(PortalSubmissionRequestFromSubmissionRequestSummary(requestSummary), commonText);
        }
        return commonText?.basisOfAccountingNA || '';

        // requestSummary is the only dependency I want
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestSummary]);

    const getAccountingDataLabel = (id: string): string => {
        const accountingGroup = defaultSubmissionOptions.accountingCollectionGroups.get(id);
        return !!accountingGroup ? (accountingGroup.name || '') : '';
    }

    const getDefaultAccountingGroupCollectionState = (id: string): boolean => {
        const accountingGroup = defaultSubmissionOptions.accountingCollectionGroups.get(id);
        return !!accountingGroup && accountingGroup.collect;
    }

    const requestSummaryText = React.useMemo(() => {
        const summary = requestSummary || defaultSubmissionOptions;

        const linkType = !requestSummary ? (commonText?.tooltipAnonymousDC || '') : 
            (requestSummary.hasShareableLink ? ConnectionSourceText.SecureUrlText : ConnectionSourceText.DirectConnectionText);
        const anonymize = summary.anonymizeCustomersAndVendors ? (commonText?.yes || '') : (commonText?.no || '');
        const allowUserUpload = summary.allowUserUpload ? (commonText?.yes || '') : (commonText?.no || '');
        const provideUserCopy = summary.provideUserCopy ? (commonText?.yes || '') : (commonText?.no || '');
        const collectFinancialStatements = 
            (!!requestSummary && (!!requestSummary.financialStatementsYearsToCollect && requestSummary.financialStatementsYearsToCollect > 0)) ||
            (!requestSummary && getDefaultAccountingGroupCollectionState(knownAccountingGroupIds.financialStatements)) ? (commonText?.yes || '') : (commonText?.no || '');
        const collectTxns = 
            (!!requestSummary && (!!requestSummary.transactionsYearsToCollect && requestSummary.transactionsYearsToCollect > 0)) ||
            (!requestSummary && getDefaultAccountingGroupCollectionState(knownAccountingGroupIds.transactions)) ? (commonText?.yes || '') : (commonText?.no || '');
        const collectReceivables = 
            (!!requestSummary && (!!requestSummary.receivablesYearsToCollect && requestSummary.receivablesYearsToCollect > 0)) ||
            (!requestSummary && getDefaultAccountingGroupCollectionState(knownAccountingGroupIds.accountsReceivable)) ? (commonText?.yes || '') : (commonText?.no || '');
        const collectPayables = 
            (!!requestSummary && (!!requestSummary.payablesYearsToCollect && requestSummary.payablesYearsToCollect > 0)) ||
            (!requestSummary && getDefaultAccountingGroupCollectionState(knownAccountingGroupIds.accountsPayable)) ? (commonText?.yes || '') : (commonText?.no || '');

        return {
            linkType,
            anonymize,
            allowUserUpload,
            provideUserCopy,
            collectFinancialStatements,
            collectTxns,
            collectReceivables,
            collectPayables,
        };

        // This is the correct dependency list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestSummary, defaultSubmissionOptions, commonText])

    const summaryRows: { prompt: string, value: string }[][] = React.useMemo(() => {
        const collectedThrough = collectingPeriodText();
        const result = [];

        result.push([
            {
                prompt: srsText?.collectedOn || '',
                value: !!submission ? FormatDate(new Date(submission.creationTime), formatStringMDYShort) : '',
            },
            {
                prompt: srsText?.preferredBasisOfAccounting || '',
                value: basisOfAccounting,
            }
        ]);
        result.push([
            {
                prompt: srsText?.originatingFrom || '',
                value: requestSummaryText.linkType,
            },
            {
                prompt: srsText?.customersAndVendorsAnonymized || '',
                value: requestSummaryText.anonymize,
            }
        ]);
        result.push([
            {
                prompt: srsText?.requestedByPortalUser || '',
                value: !!userDisplayName ? userDisplayName : (srsText?.userNotAssociatedWithSubmission || ''),
            },
            {
                prompt: srsText?.userMayUpload || '',
                value: requestSummaryText.allowUserUpload,
            }
        ]);
        result.push([
            {
                prompt: srsText?.collectionPeriod || '',
                value: collectedThrough || '',
            },
            {
                prompt: srsText?.userMayDownloadCopy || '',
                value: requestSummaryText.provideUserCopy,
            }
        ]);
        result.push([
            {
                prompt: getAccountingDataLabel(knownAccountingGroupIds.financialStatements),
                value: requestSummaryText.collectFinancialStatements,
            },
            {
                prompt: getAccountingDataLabel(knownAccountingGroupIds.transactions),
                value: requestSummaryText.collectTxns,
            }
        ]);
        result.push([
            {
                prompt: getAccountingDataLabel(knownAccountingGroupIds.accountsReceivable),
                value: requestSummaryText.collectReceivables,
            },
            {
                prompt: getAccountingDataLabel(knownAccountingGroupIds.accountsPayable),
                value: requestSummaryText.collectPayables,
            }
        ]);

        return result;

        // These are the dependencies I really want
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commonText, srsText, requestSummary, submission, basisOfAccounting, ConnectionSourceText, userDisplayName, defaultSubmissionOptions]);

    const regularContent = (): React.ReactElement => {
        if (!submission) {
            return (<></>);
        }

        return (
            <React.Fragment>
                {
                    summaryRows.map((subRow, rowIndex) => {
                        return (
                            <Row 
                                className={'header-content request-summary-row'}
                                key={`detail-row-${submission.id}-${rowIndex}`}
                            >
                                {
                                    subRow.map((column, columnIndex) => {
                                        return (
                                            <Col 
                                                xs={6} className={'spaced-row'}
                                                key={`detail-row-${submission.id}-${rowIndex}-${columnIndex}`}
                                            >
                                                <span>{column.prompt}</span>
                                                <span>{column.value}</span>
                                            </Col>
                                        )
                                    })
                                }
                            </Row>
                        );
                    })
                }
            </React.Fragment>
        );
    }

    return (
        <Container className={className} fluid>
            {loadingUsers && loadingUsersContent()}
            {!loadingUsers && regularContent()}
        </Container>
    );
}

export const RequestSummary = connect<InjectedReduxState, InjectedActionCreators, RequestSummaryProps, ApplicationState>(
    (appState: ApplicationState, props: RequestSummaryProps) => {
        const commonText = GetPortalText<PortalTextCommon>(appState, 'commonText');
        const commonImportText = GetPortalText<PortalTextCommonImport>(appState, 'commonImport');

        let userDisplayName = props.loadedUserDisplayName;

        if (!userDisplayName) {
            if (!!props.requestSummary) {
                if (!!props.requestSummary.requestingUserId) {
                    const temp = GetUserById(appState, props.requestSummary.requestingUserId);
                    if (!!temp) {
                        userDisplayName = temp.displayName;
                    }
                }
            } else {
                userDisplayName = commonText?.tooltipAnonymousDC || '';
            }
        }

        const result = {
            loadingUsers: LoadingUsers(appState),
            userDisplayName,
            fileUploadDisabled: GetDisableAdditionalDocUpload(appState),
            provideUserCopyDisabled: GetDisableProvideuserCopy(appState),
            anonymizeResultsDisabled: GetDisableAnonymizeResults(appState),
            basisOfAccountingDisabled: GetDisableBasisOfAccounting(appState),
            defaultSubmissionOptions: GetDefaultSubmissionOptions(appState, 'shareableanonymous', false, commonImportText),
            commonText,
            srsText: GetPortalText<PortalTextSubmissionRequestSummary>(appState, 'submissionRequestSummary'),
        };

        return result;
    },
)(RequestSummaryComponent);

